"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createRemoteFeatureFlagClient = void 0;
/**
 * Shared remote (server-driven) feature flag client used by both the React
 * Native mobile app and the React web app.
 *
 * Each app constructs a client via `createRemoteFeatureFlagClient(...)` and
 * supplies platform-specific dependencies:
 *   - `gatewayBaseUrl`: returns the mobile gateway base URL (must already
 *     include the `/api` suffix; the client appends `/featureflags`).
 *   - `storage`: an async `{getItem, setItem, removeItem}` adapter. The web
 *     app wraps `localStorage`; the mobile app wraps AsyncStorage. Pass
 *     `null`/omit to disable persistence entirely.
 *   - `isStaging`: optional. Returns whether this build is a staging build, so
 *     a flag's per-environment switch (`enabledStaging` vs `enabledProd`) can
 *     be applied. This MUST be the same signal the app uses to pick its
 *     staging vs production gateway URL (`reactAppIsQa` on mobile,
 *     `REACT_APP_IS_QA` on web) so the two can't drift. Defaults to `false`
 *     (production), so an app that hasn't wired this up evaluates flags as a
 *     production build (reads `enabledProd`).
 *   - `getCurrentMobileAppVersion`: optional. Returns the current app's
 *     dotted version string (e.g. "1.2.3") so flags with a
 *     `minimumMobileAppVersion` requirement can be gated. Defaults to "",
 *     which conservatively disables every flag (a flag is only enabled when
 *     the current version is at or above a non-empty `minimumMobileAppVersion`).
 *   - `getCurrentApiVersion`: optional. Returns the API server's dotted
 *     version string. Used to evaluate the file-level `cutoffApiVersion`
 *     and `minimumSupportedApiVersion` (Count-style outdated-version
 *     enforcement). Defaults to "", which means the version check is
 *     skipped entirely and status is always "ok".
 *
 * The client exposes:
 *   - `refresh()`        — GET the flag file, evaluate, cache, notify.
 *                          Returns `{flags, status, ...}`; auth code should
 *                          inspect `status` and bail on `"below-cutoff"`.
 *   - `loadCached()`     — hydrate the in-memory cache from storage.
 *   - `clear()`          — drop the cache and storage entry (call on sign-out).
 *   - `isEnabled(name)`  — synchronous read from the in-memory cache.
 *   - `getAll()`         — snapshot of the in-memory cache.
 *   - `getStatus()`      — current API-version status (from last refresh).
 *   - `subscribe(fn)`    — receive flag updates when the cache changes.
 *   - `subscribeStatus(fn)` — receive status updates from refresh().
 *   - `useFlag(name)`    — React hook that re-renders on cache updates.
 *   - `useStatus()`      — React hook for the current version status.
 */
var react_1 = require("react");
var DEFAULT_STORAGE_KEY = "RX365_REMOTE_FEATURE_FLAGS";
function compareVersions(a, b) {
    var _a, _b;
    var pa = a.split(".").map(function (part) { return parseInt(part, 10) || 0; });
    var pb = b.split(".").map(function (part) { return parseInt(part, 10) || 0; });
    var len = Math.max(pa.length, pb.length);
    for (var i = 0; i < len; i++) {
        var ai = (_a = pa[i]) !== null && _a !== void 0 ? _a : 0;
        var bi = (_b = pb[i]) !== null && _b !== void 0 ? _b : 0;
        if (ai > bi)
            return 1;
        if (ai < bi)
            return -1;
    }
    return 0;
}
function isVersionGreaterThanOrEqual(current, minimum) {
    if (!minimum)
        return true;
    return compareVersions(current, minimum) >= 0;
}
function evaluateFlags(section, currentMobileVersion, currentApiVersion, isStaging) {
    var result = {};
    if (!section || !Array.isArray(section.featureFlags))
        return result;
    for (var _i = 0, _a = section.featureFlags; _i < _a.length; _i++) {
        var flag = _a[_i];
        // Per-environment switch is the PRIMARY on/off: use this build's env field
        // when present; fall back to the legacy `enabled` flag only when the env
        // field is absent (an old AsyncStorage payload, or a flag added before these
        // fields existed). `enabled` is being deprecated and will be removed once no
        // shipped app still reads it.
        var envValue = isStaging ? flag.enabledStaging : flag.enabledProd;
        var enabled = typeof envValue === "boolean" ? envValue : flag.enabled === true;
        // Version gates are PRODUCTION-only — staging ignores them (the staging
        // build under test has the feature code, and the QA build's version is
        // inaccurate vs the real release).
        if (enabled && !isStaging) {
            // Per-flag API gate: the pharmacy backend must be new enough for this
            // feature. Blank/"0" minimumApiVersion = no backend dependency. (rx365's
            // file-level cutoff/minimumSupported are 0.0.0.0, so this gates flags
            // only — it never blocks the app or notifies the user.)
            if (!isVersionGreaterThanOrEqual(currentApiVersion, flag.minimumApiVersion)) {
                enabled = false;
            }
            else if (!flag.minimumMobileAppVersion) {
                // Blank minimumMobileAppVersion = unreleased → off in prod.
                enabled = false;
            }
            else if (!currentMobileVersion ||
                !isVersionGreaterThanOrEqual(currentMobileVersion, flag.minimumMobileAppVersion)) {
                enabled = false;
            }
        }
        result[flag.featureName] = enabled;
    }
    return result;
}
/**
 * Compute the API-version status given the current API version and the
 * file-level minimum/cutoff. Mirrors PharmacyOneCount's `processFeatureFlags`:
 *   - apiVersion < cutoff → below-cutoff (block)
 *   - apiVersion < minimum → below-minimum (warn)
 *   - otherwise → ok
 * If the section is missing or `currentApiVersion` is empty, the gate is
 * skipped (status is `"no-info"` or `"ok"` respectively).
 */
function computeStatus(section, currentApiVersion) {
    var _a, _b;
    if (!section)
        return "no-info";
    if (!currentApiVersion)
        return "ok";
    var cutoff = (_a = section.cutoffApiVersion) !== null && _a !== void 0 ? _a : "";
    if (cutoff && !isVersionGreaterThanOrEqual(currentApiVersion, cutoff)) {
        return "below-cutoff";
    }
    var minimum = (_b = section.minimumSupportedApiVersion) !== null && _b !== void 0 ? _b : "";
    if (minimum && !isVersionGreaterThanOrEqual(currentApiVersion, minimum)) {
        return "below-minimum";
    }
    return "ok";
}
function createRemoteFeatureFlagClient(options) {
    var _a, _b, _c, _d, _e, _f;
    var storageKey = (_a = options.storageKey) !== null && _a !== void 0 ? _a : DEFAULT_STORAGE_KEY;
    var storage = (_b = options.storage) !== null && _b !== void 0 ? _b : null;
    // Default to production (`false`): an app that hasn't wired up the env signal
    // is treated as a production build (reads `enabledProd`).
    var getIsStaging = (_c = options.isStaging) !== null && _c !== void 0 ? _c : (function () { return false; });
    var getMobileVersion = (_d = options.getCurrentMobileAppVersion) !== null && _d !== void 0 ? _d : (function () { return ""; });
    var getApiVersion = (_e = options.getCurrentApiVersion) !== null && _e !== void 0 ? _e : (function () { return ""; });
    var log = (_f = options.log) !== null && _f !== void 0 ? _f : (function () { });
    var cached = {};
    var status = "ok";
    var subscribers = new Set();
    var statusSubscribers = new Set();
    // Monotonic token. Any in-flight refresh/loadCached captures the token at
    // start and discards its result if the token has since changed (e.g. clear()
    // ran after sign-out, or a fresher refresh already won the race).
    var epoch = 0;
    var lastWriterEpoch = -1;
    var FETCH_TIMEOUT_MS = 8000;
    function notify() {
        var snapshot = __assign({}, cached);
        subscribers.forEach(function (cb) {
            try {
                cb(snapshot);
            }
            catch (err) {
                log("Feature flag subscriber threw", err);
            }
        });
    }
    function notifyStatus() {
        statusSubscribers.forEach(function (cb) {
            try {
                cb(status);
            }
            catch (err) {
                log("Feature flag status subscriber threw", err);
            }
        });
    }
    function setStatus(next) {
        if (next === status)
            return;
        status = next;
        notifyStatus();
    }
    function refresh() {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, Promise, function () {
            var baseUrl, url, startEpoch, controller, timeoutId, currentApiVersion, response, raw, parsed, firstPass, section, nextStatus, evaluated, _e, err_1;
            return __generator(this, function (_f) {
                switch (_f.label) {
                    case 0:
                        baseUrl = options.gatewayBaseUrl().replace(/\/+$/, "");
                        url = "".concat(baseUrl, "/featureflags");
                        startEpoch = epoch;
                        controller = typeof AbortController !== "undefined" ? new AbortController() : null;
                        timeoutId = controller
                            ? setTimeout(function () { return controller.abort(); }, FETCH_TIMEOUT_MS)
                            : null;
                        currentApiVersion = getApiVersion();
                        _f.label = 1;
                    case 1:
                        _f.trys.push([1, 8, 9, 10]);
                        return [4 /*yield*/, fetch(url, {
                                method: "GET",
                                headers: { Accept: "application/json" },
                                signal: controller === null || controller === void 0 ? void 0 : controller.signal,
                            })];
                    case 2:
                        response = _f.sent();
                        if (!response.ok) {
                            throw new Error("HTTP ".concat(response.status));
                        }
                        return [4 /*yield*/, response.text()];
                    case 3:
                        raw = _f.sent();
                        parsed = void 0;
                        try {
                            firstPass = JSON.parse(raw);
                            parsed =
                                typeof firstPass === "string"
                                    ? JSON.parse(firstPass)
                                    : firstPass;
                        }
                        catch (_g) {
                            throw new Error("Failed to parse feature flag response");
                        }
                        section = parsed.rx365FeatureFlags;
                        nextStatus = computeStatus(section, currentApiVersion);
                        evaluated = nextStatus === "below-cutoff"
                            ? {}
                            : evaluateFlags(section, getMobileVersion(), currentApiVersion, getIsStaging());
                        // If clear() ran (or another refresh started) while we were in flight,
                        // drop this result — it represents stale state.
                        if (startEpoch !== epoch) {
                            return [2 /*return*/, {
                                    flags: cached,
                                    status: status,
                                    currentApiVersion: currentApiVersion,
                                    cutoffApiVersion: (_a = section === null || section === void 0 ? void 0 : section.cutoffApiVersion) !== null && _a !== void 0 ? _a : "",
                                    minimumSupportedApiVersion: (_b = section === null || section === void 0 ? void 0 : section.minimumSupportedApiVersion) !== null && _b !== void 0 ? _b : "",
                                }];
                        }
                        cached = evaluated;
                        lastWriterEpoch = startEpoch;
                        if (!storage) return [3 /*break*/, 7];
                        _f.label = 4;
                    case 4:
                        _f.trys.push([4, 6, , 7]);
                        return [4 /*yield*/, storage.setItem(storageKey, JSON.stringify(evaluated))];
                    case 5:
                        _f.sent();
                        return [3 /*break*/, 7];
                    case 6:
                        _e = _f.sent();
                        return [3 /*break*/, 7];
                    case 7:
                        notify();
                        setStatus(nextStatus);
                        return [2 /*return*/, {
                                flags: evaluated,
                                status: nextStatus,
                                currentApiVersion: currentApiVersion,
                                cutoffApiVersion: (_c = section === null || section === void 0 ? void 0 : section.cutoffApiVersion) !== null && _c !== void 0 ? _c : "",
                                minimumSupportedApiVersion: (_d = section === null || section === void 0 ? void 0 : section.minimumSupportedApiVersion) !== null && _d !== void 0 ? _d : "",
                            }];
                    case 8:
                        err_1 = _f.sent();
                        log("Failed to refresh remote feature flags", err_1);
                        return [2 /*return*/, {
                                flags: cached,
                                status: status,
                                currentApiVersion: currentApiVersion,
                                cutoffApiVersion: "",
                                minimumSupportedApiVersion: "",
                            }];
                    case 9:
                        if (timeoutId)
                            clearTimeout(timeoutId);
                        return [7 /*endfinally*/];
                    case 10: return [2 /*return*/];
                }
            });
        });
    }
    function loadCached() {
        return __awaiter(this, void 0, Promise, function () {
            var startEpoch, raw, parsed, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!storage)
                            return [2 /*return*/, cached];
                        startEpoch = epoch;
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, storage.getItem(storageKey)];
                    case 2:
                        raw = _b.sent();
                        if (raw) {
                            parsed = JSON.parse(raw);
                            if (parsed &&
                                typeof parsed === "object" &&
                                !Array.isArray(parsed) &&
                                // Don't stomp a fresher refresh that won the race or a clear() that
                                // happened mid-read.
                                startEpoch === epoch &&
                                lastWriterEpoch < startEpoch) {
                                cached = parsed;
                                lastWriterEpoch = startEpoch;
                                notify();
                            }
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        _a = _b.sent();
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/, cached];
                }
            });
        });
    }
    function clear() {
        return __awaiter(this, void 0, Promise, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        epoch += 1;
                        cached = {};
                        if (!storage) return [3 /*break*/, 4];
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, storage.removeItem(storageKey)];
                    case 2:
                        _b.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        _a = _b.sent();
                        return [3 /*break*/, 4];
                    case 4:
                        notify();
                        setStatus("ok");
                        return [2 /*return*/];
                }
            });
        });
    }
    function isEnabled(featureName, defaultValue) {
        if (defaultValue === void 0) { defaultValue = false; }
        var value = cached[featureName];
        return typeof value === "boolean" ? value : defaultValue;
    }
    function getAll() {
        return __assign({}, cached);
    }
    function getStatus() {
        return status;
    }
    function subscribe(cb) {
        subscribers.add(cb);
        return function () {
            subscribers.delete(cb);
        };
    }
    function subscribeStatus(cb) {
        statusSubscribers.add(cb);
        return function () {
            statusSubscribers.delete(cb);
        };
    }
    function useFlag(featureName, defaultValue) {
        if (defaultValue === void 0) { defaultValue = false; }
        var _a = (0, react_1.useState)(function () {
            return isEnabled(featureName, defaultValue);
        }), value = _a[0], setValue = _a[1];
        (0, react_1.useEffect)(function () {
            var handler = function (flags) {
                var next = flags[featureName];
                setValue(typeof next === "boolean" ? next : defaultValue);
            };
            var unsubscribe = subscribe(handler);
            handler(cached);
            return unsubscribe;
        }, [featureName, defaultValue]);
        return value;
    }
    function useStatus() {
        var _a = (0, react_1.useState)(status), value = _a[0], setValue = _a[1];
        (0, react_1.useEffect)(function () {
            setValue(status);
            return subscribeStatus(setValue);
        }, []);
        return value;
    }
    return {
        refresh: refresh,
        loadCached: loadCached,
        clear: clear,
        isEnabled: isEnabled,
        getAll: getAll,
        getStatus: getStatus,
        subscribe: subscribe,
        subscribeStatus: subscribeStatus,
        useFlag: useFlag,
        useStatus: useStatus,
    };
}
exports.createRemoteFeatureFlagClient = createRemoteFeatureFlagClient;
